import React, { Component, createContext } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import queryString from 'query-string';
import { resetStoreAndLogout } from 'setup';
import omit from 'lodash/omit';
import { currentUser, allLocations } from 'modules/utils/queries';
import { Sizes, lStorage, cookiesHelper } from 'modules/utils';
import { withRouter } from 'react-router-dom';
import withContext from '../hoc/withContext';
import { getLanguageCode } from '../localization';

export const AppContext = createContext();
export const AppContextProvider = AppContext.Provider;
export const AppConsumer = AppContext.Consumer;
export const withAppContext = withContext(AppConsumer);

class AppProvider extends Component {
	constructor(props) {
		super(props);
		const { history } = props;

		// This will listen for any change in the pathnmae
		history.listen((location, action) => {
			this.refetchQueries(action, location);
		});
	}

	state = {
		currentUser: lStorage.get({ key: 'current-user' }).user,
		newReports: 0,
		restaurantId: 0,
		navItems: [],
		hasNavItems: true,
		chainLocations: [],
		location: {},
		selectedLocation: {},
		displayForcedModifierPrices: false,
		iframePointerEvents: false,
		isPrintable: queryString.parse(window.location.search).printable === 'true',
		isZReport: queryString.parse(window.location.search).report === 'zReport',
		showSideNav: window.innerWidth >= Sizes.LARGE,
		languageCode:
			cookiesHelper.get('CP4_LOCALE') || getLanguageCode(navigator.language),
	};

	static propTypes = {
		userData: PropTypes.object,
		allLocations: PropTypes.shape({
			location: PropTypes.object,
		}),
		history: PropTypes.shape({
			listen: PropTypes.func,
		}),
		location: PropTypes.object,
		children: PropTypes.node,
	};

	// Here we compute our state object based on the props that come down
	// from our GraphQL query.
	static getDerivedStateFromProps(nextProps) {
		const { userData, allLocations } = nextProps;

		if (
			userData &&
			!userData.loading &&
			allLocations &&
			userData.currentUser &&
			!allLocations.loading
		) {
			const { currentUser } = userData;
			const { location, chainLocations } = allLocations;

			if (!currentUser) {
				return resetStoreAndLogout();
			}

			const { user } = lStorage.get({ key: 'current-user' });

			if (!user) {
				return null;
			}

			// If cached dataname is different than dataname in token, refresh page
			if (user.dataname !== location.dataname) {
				window.location.reload();
			}

			const {
				decimalChar,
				thousandsChar,
				monetarySymbol,
				decimalPlaces,
				monetarySymbolPosition,
				businessInfo,
				locationCdpConfig,
				dataLockSyncerOrderSync,
			} = location;
			const {
				countryRegionCode,
				accessWhiteList,
				startDayOfWeek,
				restrictedUser,
				languagePack,
				newReports,
				dateFormat,
			} = currentUser;

			user.monetarySymbol = monetarySymbol;
			user.position = monetarySymbolPosition;
			user.primaryCurrencyCode = countryRegionCode;
			user.dateFormat = dateFormat;
			user.thousandsChar = thousandsChar;
			user.decimalChar = decimalChar === 'none' ? '' : decimalChar;
			user.decimalPlaces = decimalPlaces;
			user.accessWhiteList = accessWhiteList;
			user.restrictedUser = restrictedUser;
			user.languagePack = { ...languagePack };
			user.navItems = currentUser.navItems;
			user.businessInfo = businessInfo;
			user.locationCdpConfig = locationCdpConfig;
			user.dataLockSyncerOrderSync = dataLockSyncerOrderSync;
			user.newReports = newReports !== undefined ? newReports : user.newReports;
			user.startDayOfWeek =
				startDayOfWeek !== undefined ? startDayOfWeek : user.startDayOfWeek;
			user.trialInfo = currentUser.trialInfo;

			lStorage.set({ key: 'current-user', value: { user: { ...user } } });
			cookiesHelper.set(
				'CP4_LOCALE',
				getLanguageCode(languagePack.googleCode),
				30,
				'/'
			);

			return {
				currentUser: omit(currentUser, 'navItems'),
				newReports: +user.newReports,
				restaurantId: location.restaurantId,
				navItems: currentUser.navItems,
				hasNavItems:
					!!currentUser.navItems &&
					currentUser.navItems.filter(({ hide }) => !hide).length > 0,
				location,
				chainLocations,
				selectedLocation: location,
				displayForcedModifierPrices: !!location.displayForcedModifierPrices,
				isManageUser: Boolean(currentUser.lavuAdmin),
				languageCode: getLanguageCode(languagePack.googleCode),
			};
		} else if (!userData) {
			const { user: currentUser } = lStorage.get({ key: 'current-user' });
			return {
				navItems:
					currentUser && currentUser.navItems ? currentUser.navItems : [],
				currentUser,
				isManageUser: Boolean(currentUser && currentUser.lavuAdmin),
			};
		}

		return null;
	}

	// Given that a lot of the settings available for CP4 can only be modified
	// in old CP, we can't always trust in the current data returned by the queries currentUser
	// and allLocations, so we need to request new information frequently. To solve this
	// both queries have set the fetchPolicy to network-only to be updated after re-render
	// of the component, but with the current version of apollo-client and react-apollo
	// this behavior is not happening (here in this issue you have more information
	// https://github.com/apollographql/react-apollo/issues/556).
	// For that reason this workaround has been implemented to refetch both queries
	// when the user navigates out from the Settings module in CP4.
	refetchQueries = (action, nextLocation) => {
		const { allLocations, userData, location } = this.props;
		const isSettingsPath = location.pathname.split('/')[1] === 'settings';
		const isNavigationOutOfSettings =
			nextLocation.pathname.split('/')[1] !== 'settings';

		// The last condition prevents to refetch the queries
		// when a <Redirect /> has cause a change in the pathname
		if (
			userData &&
			allLocations &&
			userData.currentUser &&
			!userData.loading &&
			!allLocations.loading &&
			action !== 'REPLACE' &&
			isSettingsPath &&
			isNavigationOutOfSettings
		) {
			userData.refetch();
			allLocations.refetch();
		}
	};

	updateContext = newContext => {
		this.setState(newContext);
	};

	render() {
		const { children, userData } = this.props;
		const contextValue = {
			...this.state,
			updateContext: this.updateContext,
			refetchCurrentUser: userData ? userData.refetch : Function.prototype,
		};

		return (
			<AppContextProvider value={contextValue}>{children}</AppContextProvider>
		);
	}
}

// Skip the query if the user doesn't have a key in locaStorage.
// or the dataname stored is empty (Lavu Admins)
const skipQuery = () =>
	!cookiesHelper.get('LAVU_TOKEN') ||
	queryString.parse(window.location.search).newLogin;

export default compose(
	withRouter,
	graphql(currentUser, {
		name: 'userData',
		skip: skipQuery,
		options: { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
	}),
	graphql(allLocations, {
		skip: skipQuery,
		name: 'allLocations',
		options: { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
	})
)(AppProvider);
